import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, TargetIcon, TrendingUpIcon, CpuIcon, GraduationCapIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Container, StatBlock, Reveal } from '../components/ui/Primitives';
import { HeroArcs, PlatformMock } from '../components/sections/HeroVisual';
import { ExplainerVideo } from '../components/sections/ExplainerVideo';
import { PressStrip } from '../components/sections/PressStrip';
import { Faqs } from '../components/sections/Faqs';
import { GENERAL_FAQS } from '../data/faqs';
import { nextWebinar, SESSION_TIME } from '../data/webinars';
import { sessionDateLabel } from '../components/webinars/SessionTime';
import { SERVICE_LINES, METHOD_STEPS, SECTORS } from '../data/services';
import { ARTICLES } from '../data/insights';
import { RESOURCES } from '../data/resources';
const CAPABILITY_CARDS = [{
  icon: TargetIcon,
  title: 'Market decisions',
  detail: 'Size a market from the bottom up, name the competitors and their terms, and cost the entry route.',
  to: '/services/intelligence'
}, {
  icon: TrendingUpIcon,
  title: 'Demand and revenue',
  detail: 'Choose the segments, price the offer and build the pipeline that makes demand arrive predictably.',
  to: '/services/growth'
}, {
  icon: CpuIcon,
  title: 'AI adoption',
  detail: 'Put AI where it moves a number your board already watches, with the governance written first.',
  to: '/services/ai'
}, {
  icon: GraduationCapIcon,
  title: 'Capability building',
  detail: 'Hand over the method, the models and the training so the work continues without us.',
  to: '/augmented-intelligence'
}];
export function Home() {
  const reduced = useReducedMotion();
  const featured = ARTICLES.slice(0, 3);
  const playbook = RESOURCES[0];
  const nextSession = nextWebinar();
  return <>
      {/* Hero */}
      <section className="relative isolate overflow-hidden surface-dark" data-id="element-893" data-mp="1kkz0v9khhnqq">
        <HeroArcs className="pointer-events-none absolute -right-10 top-0 h-[420px] w-[420px] opacity-90" data-id="element-894" data-mp="1kmtk8jg3x7sp" />
        <Container className="relative py-16 lg:py-24" data-id="element-895" data-mp="1ojziwpv50mco">
          <div className="grid items-center gap-14 lg:grid-cols-[1fr_1.05fr]" data-id="element-896" data-mp="1anulptp22sjf">
            <motion.div initial={reduced ? false : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: 'easeOut'
          }} data-id="element-897" data-mp="1fkukrknhmv11">
              <h1 className="font-display text-[40px] font-light leading-[1.06] text-white sm:text-[56px] lg:text-[64px]" data-id="element-898" data-mp="2899y82qz15a9">
                Empowering the organisations shaping African markets
              </h1>
              <p className="mt-7 max-w-lg text-[17px] leading-relaxed text-white/72 sm:text-[19px]" data-id="element-899" data-mp="1yixda9f8va3l">
                Market intelligence, data led growth and augmented intelligence, delivered as evidence you can
                take into the room where the decision is made.
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row" data-id="element-900" data-mp="fkixp27eeuq6">
                <Button to="/contact" size="lg" shape="pill" data-id="element-901" data-mp="an937855jsvh">
                  Book a discovery call
                </Button>
                <Button to="/assessment" variant="reversed" size="lg" shape="pill" data-id="element-902" data-mp="ocud7t0z4xrd">
                  Take the AI readiness assessment
                </Button>
              </div>
            </motion.div>

            <motion.div className="flex justify-center lg:justify-end" initial={reduced ? false : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: 'easeOut',
            delay: 0.12
          }} data-id="element-903" data-mp="3st5bjjuvcc9a">
              <PlatformMock data-id="element-904" data-mp="ni56whduvqn" />
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Press */}
      <PressStrip data-id="element-905" data-mp="1h18echnd0a75" />

      {/* Capability cards */}
      <section aria-labelledby="capabilities" className="bg-charcoal" data-id="element-906" data-mp="1c4855xv7qrbt">
        <Container className="py-16 lg:py-24" data-id="element-907" data-mp="1c62ppb2kohfd">
          <h2 id="capabilities" className="max-w-4xl font-display text-[30px] font-light leading-tight text-white sm:text-[42px]" data-id="element-908" data-mp="27ey9vwxj1zyl">
            Intelligence, method and technology for:
          </h2>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-909" data-mp="2harkvbvllle9">
            {CAPABILITY_CARDS.map((card, index) => {
            const Icon = card.icon;
            return <Reveal key={card.title} delay={index * 0.06} className="h-full" data-id="element-910" data-mp="2qch968u6o47t" data-mpi={index}>
                  <Link to={card.to} className="group flex h-full flex-col border border-white/20 p-7 transition-colors duration-200 hover:border-gold" data-id="element-911" data-mp="2vaj0ti5odbuw">
                    <Icon className="h-8 w-8 text-white" aria-hidden="true" strokeWidth={1.4} data-id="element-912" data-mp="350vkz99kv30i" />
                    <h3 className="mt-6 font-display text-[24px] leading-snug text-white" data-id="element-913" data-mp="3a2b7z6vbfjlj">{card.title}</h3>
                    <p className="mt-4 text-[15px] leading-relaxed text-white/70" data-id="element-914" data-mp="3e2lag9jwfrvn">{card.detail}</p>
                    <span className="mt-auto inline-flex items-center gap-2 pt-7 text-[14px] font-medium text-gold" data-id="element-915" data-mp="12gb5kiotcmvh">
                      Learn more
                      <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-916" data-mp="tds2pyl12a98" />
                    </span>
                  </Link>
                </Reveal>;
          })}
          </div>
        </Container>
      </section>

      {/* Animated explainer */}
      <section aria-labelledby="explainer" className="bg-charcoal" data-id="element-917" data-mp="1ccmh36gkx8ov">
        <Container className="py-16 lg:py-24" data-id="element-918" data-mp="1cat8hlmeoee8">
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-919" data-mp="17d1iwy98h4d7">
            <div className="max-w-2xl" data-id="element-920" data-mp="24t7196svxa6g">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-921" data-mp="24v1kmgoicu8f">Watch</p>
              <h2 id="explainer" className="mt-4 font-display text-[32px] leading-tight text-ink sm:text-[44px]" data-id="element-922" data-mp="2015rbrj6vvjl">
                How Cavalla Solution works
              </h2>
              <p className="mt-5 text-[17px] text-body" data-id="element-923" data-mp="25qigcdi7d5l">
                Half a minute on how an engagement runs, from scattered evidence to a decision somebody owns.
              </p>
            </div>
          </div>
          <div className="mt-10" data-id="element-924" data-mp="3pikpwsi7cfk2">
            <ExplainerVideo data-id="element-925" data-mp="3phj69zn4ekqq" />
          </div>
        </Container>
      </section>

      {/* Method */}
      <section aria-labelledby="method" className="bg-charcoal" data-id="element-926" data-mp="3aoy241obp1v5">
        <Container className="py-16 lg:py-24" data-id="element-927" data-mp="3fn9d4fqctiey">
          <div className="grid gap-14 lg:grid-cols-[0.85fr_1.15fr]" data-id="element-928" data-mp="31m6n5rw8nnyr">
            <div className="lg:sticky lg:top-32 lg:self-start" data-id="element-929" data-mp="31nzxpi9z2sc1">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-930" data-mp="36m1hk5f82vok">Our method</p>
              <h2 id="method" className="mt-4 font-display text-[32px] leading-tight text-ink sm:text-[44px]" data-id="element-931" data-mp="zvv7qcc1a53y">
                Four steps, run on every engagement
              </h2>
              <p className="mt-5 max-w-md text-[17px] text-body" data-id="element-932" data-mp="1eymxfgft5wpz">
                Augmented intelligence is the deliberate pairing of machine analysis with human judgement,
                arranged so a person stays accountable for the decision.
              </p>
              <Button to="/augmented-intelligence" variant="secondary" shape="pill" className="mt-8" data-id="element-933" data-mp="1iz6vqnc4b6dp">
                Walk through the method
              </Button>
            </div>

            <ol className="divide-y divide-line border-t border-line" data-id="element-934" data-mp="1xq5vr9yw0mlo">
              {METHOD_STEPS.map((step, index) => <li key={step.id} className="grid gap-4 py-8 sm:grid-cols-[64px_1fr]" data-id="element-935" data-mp="26ohl78o0pecj" data-mpi={index}>
                  <span className="font-display text-[26px] leading-none text-gold" data-id="element-936" data-mp="22n645rj7m1fy">0{index + 1}</span>
                  <div data-id="element-937" data-mp="caqd4dlc72j7">
                    <h3 className="font-display text-[24px] leading-snug text-ink" data-id="element-938" data-mp="c9yl1cqy7mxo">{step.title}</h3>
                    <p className="mt-3 max-w-prose text-[16px] text-body" data-id="element-939" data-mp="89wthwexg9w4">{step.summary}</p>
                    <p className="mt-4 text-[13px] uppercase tracking-[0.12em] text-muted" data-id="element-940" data-mp="wul5e15z7ql2">
                      You receive: <span className="normal-case tracking-normal text-body" data-id="element-941" data-mp="11scy2xeg3gqb">{step.output}</span>
                    </p>
                  </div>
                </li>)}
            </ol>
          </div>
        </Container>
      </section>

      {/* Statistics */}
      <section aria-labelledby="numbers" className="bg-mist" data-id="element-942" data-mp="dv7clmjzglw5">
        <Container className="py-16 lg:py-20" data-id="element-943" data-mp="dx1vywflw5y4">
          <h2 id="numbers" className="font-display text-[28px] leading-tight text-ink sm:text-[36px]" data-id="element-944" data-mp="itsb9hjl6xih">
            The market we work in, in figures
          </h2>
          <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4" data-id="element-945" data-mp="my04ns0zp1n1">
            <Reveal data-id="element-946" data-mp="rwjqlsfl2dfe"><StatBlock value={16.5} decimals={1} prefix="$" suffix="bn" label="Africa AI market by 2030" source="Market forecast, 2025" data-id="element-947" data-mp="rw9z20ew3y7l" /></Reveal>
            <Reveal delay={0.05} data-id="element-948" data-mp="239vpdowhk5up"><StatBlock value={2.9} decimals={1} suffix="x" label="Growth in enterprise AI spend since 2023" source="Cavalla analysis, 2026" data-id="element-949" data-mp="23941l094rcez" /></Reveal>
            <Reveal delay={0.1} data-id="element-950" data-mp="2dfww9xx2mnrm"><StatBlock value={61} suffix="%" label="Of programmes stall on data foundations, not models" source="Cavalla client review, 2026" data-id="element-951" data-mp="28f977hsekh01" /></Reveal>
            <Reveal delay={0.15} data-id="element-952" data-mp="23mpu0lxmnnvr"><StatBlock value={10} label="Priority sectors under continuous coverage" source="Cavalla sector coverage" data-id="element-953" data-mp="23m68ywcokjdi" /></Reveal>
          </div>
        </Container>
      </section>

      {/* Service lines */}
      <section aria-labelledby="lines" className="bg-charcoal" data-id="element-954" data-mp="1ul85qga2gfvz">
        <Container className="py-16 lg:py-24" data-id="element-955" data-mp="1pmwuc144hxbn">
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-956" data-mp="1kp5379bayf8i">
            <div className="max-w-2xl" data-id="element-957" data-mp="1krhkdk98klki">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-958" data-mp="1gobispx2jikd">Three lines</p>
              <h2 id="lines" className="mt-4 font-display text-[32px] leading-tight text-ink sm:text-[44px]" data-id="element-959" data-mp="3f3pgv217haie">
                Start with the question you actually have
              </h2>
            </div>
            <Button to="/services" variant="secondary" shape="pill" size="sm" data-id="element-960" data-mp="cnjwif91ooym">
              How we work
            </Button>
          </div>

          <div className="mt-12 grid gap-px bg-line lg:grid-cols-3" data-id="element-961" data-mp="8p2mnny8p0di">
            {SERVICE_LINES.map(line => <article key={line.id} className="flex flex-col bg-white p-8" data-id="element-962" data-mp="hnods7hs1npw">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-963" data-mp="hllesxynck91">{line.short}</p>
                <h3 className="mt-4 font-display text-[27px] leading-tight text-ink" data-id="element-964" data-mp="3q1527gcmz8g6">{line.name}</h3>
                <p className="mt-4 text-[16px] text-body" data-id="element-965" data-mp="3l2k35k94eutb">{line.promise}</p>
                <ul className="mt-6 space-y-2.5 text-[14px] text-muted" data-id="element-966" data-mp="3p7w5lidmcsz">
                  {line.subServices.map(sub => <li key={sub.name} className="flex gap-2.5" data-id="element-967" data-mp="37d052rqhilqc">
                      <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-gold" data-id="element-968" data-mp="37bf7et8vimo9" />
                      {sub.name}
                    </li>)}
                </ul>
                <div className="mt-auto pt-8" data-id="element-969" data-mp="1hujqr8zigr1t">
                  <Link to={`/services/${line.id}`} className="inline-flex items-center gap-2 text-[15px] font-medium text-royal underline-offset-4 hover:underline" data-id="element-970" data-mp="1mmbap0o5vyiz">
                    Explore this line
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-971" data-mp="1cup1f9vdw9iv" />
                  </Link>
                </div>
              </article>)}
          </div>

          {/* Solution teaser, distinct from the three advisory lines */}
          <Reveal className="mt-8" data-id="element-972" data-mp="1rmh1yfqirj0j">
            <Link to="/cavalla-gov" className="group flex flex-col gap-6 rounded-md border border-line bg-panel p-8 transition-colors duration-200 hover:border-gold sm:flex-row sm:items-center sm:justify-between" data-id="element-973" data-mp="1rleekhsenypd">
              <div className="max-w-2xl" data-id="element-974" data-mp="l654kgviu560">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold" data-id="element-975" data-mp="l6wy7m5k5stn">
                  Our platform for government
                </p>
                <h3 className="mt-4 font-display text-[27px] leading-tight text-ink" data-id="element-976" data-mp="2hvetf1hs33nj">Cavalla Gov</h3>
                <p className="mt-3 text-[16px] text-body" data-id="element-977" data-mp="2d46lyb00dsd0">
                  One live picture of how a national development plan is being delivered, and where development
                  partner funding lands against it.
                </p>
              </div>
              <span className="inline-flex shrink-0 items-center gap-2 text-[15px] font-medium text-royal" data-id="element-978" data-mp="1z56ukvl42zd5">
                See the platform
                <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-979" data-mp="1u75ac7by8u03" />
              </span>
            </Link>
          </Reveal>
        </Container>
      </section>

      {/* Sectors */}
      <section aria-labelledby="sectors" className="surface-dark" data-id="element-980" data-mp="3kjl0zk5wtqwb">
        <Container className="py-16 lg:py-20" data-id="element-981" data-mp="16b0lhjjip4jj">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]" data-id="element-982" data-mp="16a92d873wza5">
            <div data-id="element-983" data-mp="1b924k4gy13v0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-984" data-mp="1b8kbththp7ru">Coverage</p>
              <h2 id="sectors" className="mt-4 font-display text-[30px] leading-tight text-white sm:text-[40px]" data-id="element-985" data-mp="1g1v8iq4l8861">
                Ten priority sectors, tracked continuously
              </h2>
              <p className="mt-5 max-w-md text-[16px] text-white/70" data-id="element-986" data-mp="ncy0rgy05trj">
                We hold a standing view of demand, policy and buyer behaviour in each of these, refreshed monthly
                rather than assembled when a client asks.
              </p>
            </div>
            <ul className="grid grid-cols-2 gap-px self-start bg-white/15 sm:grid-cols-3" data-id="element-987" data-mp="11c795ufyvjky">
              {SECTORS.map(sector => <li key={sector} className="bg-panel px-5 py-6 text-[16px] text-white/85" data-id="element-988" data-mp="27r85404zs1rj">
                  {sector}
                </li>)}
            </ul>
          </div>
        </Container>
      </section>

      {/* Insights */}
      <section aria-labelledby="insights" className="bg-charcoal" data-id="element-989" data-mp="26aknvm7jsriw">
        <Container className="py-16 lg:py-24" data-id="element-990" data-mp="26cf62srfq5ja">
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-991" data-mp="2kavf4qcrs84l">
            <div data-id="element-992" data-mp="2g8ryzof5ofcz">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-993" data-mp="2g88tx8altlgd">Insights</p>
              <h2 id="insights" className="mt-4 font-display text-[32px] leading-tight text-ink sm:text-[44px]" data-id="element-994" data-mp="2u804ez1wwjpm">
                Analysis for people who have to act on it
              </h2>
            </div>
            <Button to="/insights" variant="secondary" shape="pill" size="sm" data-id="element-995" data-mp="33ymc873e05xr">
              All insights
            </Button>
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-3" data-id="element-996" data-mp="2z1cpx0baoae5">
            {featured.map((article, index) => <Reveal key={article.slug} delay={index * 0.06} className="h-full" data-id="element-997" data-mp="3cwoip25w3qsf" data-mpi={index}>
                <article className="group flex h-full flex-col" data-id="element-998" data-mp="381rbm0s67ri6">
                  <Link to={`/insights/${article.slug}`} className="block overflow-hidden" data-id="element-999" data-mp="3rmrlqfkjkj7u">
                    <img src={article.heroImage} alt={article.heroAlt} loading="lazy" className="aspect-[3/2] w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]" data-id="element-1000" data-mp="fopvatrdora" />
                  </Link>
                  <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.14em] text-gold" data-id="element-1001" data-mp="38tnokk847o8h">
                    {article.sectors[0]} · {article.readTime} minute read
                  </p>
                  <h3 className="mt-3 font-display text-[22px] leading-snug text-ink" data-id="element-1002" data-mp="2yync5iyunlzv">
                    <Link to={`/insights/${article.slug}`} className="underline-offset-4 hover:underline" data-id="element-1003" data-mp="33wx7puohkom0">
                      {article.title}
                    </Link>
                  </h3>
                  <p className="mt-3 text-[15px] text-body" data-id="element-1004" data-mp="2uuflhg8tzvz0">{article.excerpt}</p>
                </article>
              </Reveal>)}
          </div>
        </Container>
      </section>

      {/* eBook */}
      <section aria-labelledby="ebook" className="bg-champagne" data-id="element-1005" data-mp="vmz5u7fvutt4">
        <Container className="py-16 lg:py-20" data-id="element-1006" data-mp="qoxnxpu710j8">
          <div className="grid items-center gap-12 lg:grid-cols-[0.7fr_1.3fr]" data-id="element-1007" data-mp="qpp7216lt5sm">
            <img src={playbook.cover} alt={playbook.coverAlt} loading="lazy" className="mx-auto w-full max-w-[300px]" data-id="element-1008" data-mp="mp5elawz6qct" />
            <div data-id="element-1009" data-mp="1eg33s504vstj">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-1010" data-mp="1efjj4gj3mqbt">Free eBook</p>
              <h2 id="ebook" className="mt-4 font-display text-[30px] leading-tight text-ink sm:text-[40px]" data-id="element-1011" data-mp="1abllka9f9nkv">
                {playbook.title}
              </h2>
              <p className="mt-5 max-w-xl text-[17px] text-body" data-id="element-1012" data-mp="3v3xdf2fsch36">{playbook.summary}</p>
              <ul className="mt-6 space-y-2.5 text-[15px] text-body" data-id="element-1013" data-mp="3q41az7mqkxpq">
                {playbook.whatIsInside.slice(0, 3).map(item => <li key={item} className="flex gap-3" data-id="element-1014" data-mp="25o39rs701i13">
                    <span aria-hidden="true" className="mt-2.5 h-1 w-1 shrink-0 rounded-full bg-gold" data-id="element-1015" data-mp="2al3fu33umks7" />
                    {item}
                  </li>)}
              </ul>
              <Button to={`/resources/${playbook.slug}`} size="lg" shape="pill" className="mt-8" data-id="element-1016" data-mp="2jdolqimdswzm">
                Download the playbook
              </Button>
            </div>
          </div>
        </Container>
      </section>

      {/* Client proof */}
      <section aria-labelledby="proof" className="bg-charcoal" data-id="element-1017" data-mp="2ta9p6zir601c">
        <Container className="py-16 lg:py-20" data-id="element-1018" data-mp="2yaxrihcfm3av">
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-1019" data-mp="2y9332t1ybt21">
            <div className="max-w-2xl" data-id="element-1020" data-mp="32c985xdbve6x">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-1021" data-mp="13x69oujy8d4c">Client words</p>
              <h2 id="proof" className="mt-4 font-display text-[30px] leading-tight text-ink sm:text-[40px]" data-id="element-1022" data-mp="17yhzp16qj0d2">
                We publish results, not promises
              </h2>
              <p className="mt-5 text-[16px] text-body" data-id="element-1023" data-mp="p06fv33vdyv7">
                Case studies from our current engagements will appear here as each one completes, with the client
                named and the numbers agreed.
              </p>
            </div>
          </div>
          <div className="mt-10 grid gap-px bg-line sm:grid-cols-3" data-id="element-1024" data-mp="ttri57chxf07">
            {['Banking, West Africa', 'Government, East Africa', 'NGO, multi country'].map(label => <div key={label} className="bg-mist p-8" data-id="element-1025" data-mp="15c1kgdpuomr">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-1026" data-mp="16x0ei72ctqf">{label}</p>
                <p className="mt-5 font-display text-[20px] leading-snug text-muted/75" data-id="element-1027" data-mp="a58oacgjpdf6">
                  Case study in preparation
                </p>
                <p className="mt-3 text-[14px] text-muted" data-id="element-1028" data-mp="1qi7b7dete6i2">Published on completion.</p>
              </div>)}
          </div>
        </Container>
      </section>

      {/* Next webinar */}
      {nextSession && <section aria-labelledby="next-webinar" className="border-y border-line bg-champagne" data-id="element-1029" data-mp="1u3irhsqfiyyg">
          <Container className="py-11" data-id="element-1030" data-mp="1y4cdggkba3op">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-1031" data-mp="1k3rwzr1vcjyd">
              <div className="max-w-2xl" data-id="element-1032" data-mp="1p1tew8nk6d89">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold" data-id="element-1033" data-mp="1p2v1nadxso5t">
                  Next webinar, free to attend
                </p>
                <h2 id="next-webinar" className="mt-3 font-display text-[23px] leading-snug text-ink sm:text-[27px]" data-id="element-1034" data-mp="2hrikkzb7r1g7">
                  {nextSession.title}
                </h2>
                <p className="mt-3 text-[15px] text-body" data-id="element-1035" data-mp="2817cr1oes841">
                  {sessionDateLabel(nextSession)}, {SESSION_TIME.gmt}. For {nextSession.audience.toLowerCase()}.
                </p>
              </div>
              <Button to={`/webinars/${nextSession.slug}`} size="lg" shape="pill" className="shrink-0" data-id="element-1036" data-mp="30h8dthzbvf60">
                Reserve a place
              </Button>
            </div>
          </Container>
        </section>}

      {/* Questions */}
      <Faqs id="home-faqs" kicker="Common questions" title="What people ask us first." items={GENERAL_FAQS} tone="mist" data-id="element-1037" data-mp="zyab3xrct75w" />

      {/* Closing call to action */}
      <section className="relative isolate overflow-hidden surface-dark" data-id="element-1038" data-mp="r4ni2fej6i0e">
        <HeroArcs className="pointer-events-none absolute -right-16 -top-8 h-[300px] w-[300px] opacity-70" data-id="element-1039" data-mp="c869tx1n1ji2" />
        <Container className="relative py-16 lg:py-20" data-id="element-1040" data-mp="h93qg57025hg">
          <div className="flex flex-col items-start justify-between gap-9 lg:flex-row lg:items-center" data-id="element-1041" data-mp="38sw2s6he8rj">
            <div className="max-w-xl" data-id="element-1042" data-mp="8744cyh4ob7n">
              <h2 className="font-display text-[30px] leading-tight text-white sm:text-[40px]" data-id="element-1043" data-mp="885sa3j8ss6s">
                Tell us the decision you are facing
              </h2>
              <p className="mt-5 text-[17px] text-white/72" data-id="element-1044" data-mp="22lwzyxi0h1zl">
                A discovery call lasts thirty minutes. You leave it with a view on whether we are the right firm
                for the problem, and a first read on how we would approach it.
              </p>
            </div>
            <div className="flex shrink-0 flex-col gap-3 sm:flex-row" data-id="element-1045" data-mp="2lifbzcyj1d7v">
              <Button to="/contact" size="lg" shape="pill" data-id="element-1046" data-mp="2ljqgjezsqp01">
                Book a discovery call
              </Button>
              <Button to="/services" variant="reversed" size="lg" shape="pill" data-id="element-1047" data-mp="2vfthwimtqjlx">
                Explore our services
              </Button>
            </div>
          </div>
        </Container>
      </section>
    </>;
}